// src/data/lookups.ts
// -------------------
// Repository for the centralised dropdown lists — one `lookup_options` table
// keyed by `category` ('customer_type' | 'sales_group' | 'warehouse' | ...),
// replacing the old per-list `customer_types` / `sales_groups` tables. Supplies
// the dropdown options across the admin screens and is edited by the "Dropdown
// Lists" management screen. Design: docs/dropdown-lookups-plan.md. Typed via
// getTypedClient().
//
// Storage model: MOST consumer records (e.g. shelters.customer_type /
// sales_group) store the VALUE STRING; this table only supplies options (not an
// FK). `external_ref` holds the option's Unleashed identifier. THE ONE EXCEPTION
// is shelters.default_warehouse, which stores the CODE (external_ref, e.g. "W3"),
// not the name — the warehouse is an Unleashed-native identifier, so storing the
// stable code is rename-safe + translation-free for the Unleashed push (db-58,
// 9 July 2026). Rule going forward: store external_ref when an option HAS one that
// is its true external identifier; store the value string otherwise.

import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

// Known categories. `string` is accepted everywhere so new lists don't need a
// type change, but these document the ones in use.
export type LookupCategory = "customer_type" | "sales_group" | "warehouse" | "state";

export interface LookupOption {
  id: string;
  category: string;
  value: string;
  sort_order: number;
  external_ref: string | null;
  is_system: boolean;
  // Shown in the Request-platform dropdowns. false = reference/mapping only
  // (e.g. Unleashed customer-type codes) — surfaced in the manager's "show all".
  app_visible: boolean;
  deactivated: boolean;
  // Broad grouping for customer_type values (db-124): DFV Shelter / DFV Support
  // Org. / First Nation / Other. NULL for other lists (sales_group/warehouse).
  // Optional so test fixtures / older callers that don't set it still type-check.
  option_category?: string | null;
}

// Options for a category, ordered by sort_order then value. Active only by
// default; pass { includeInactive: true } for the management screen (so
// deactivated rows can be shown + reactivated). Null on error / no client.
export async function listLookupOptions(
  category: string,
  opts: { includeInactive?: boolean; includeHidden?: boolean } = {}
): Promise<LookupOption[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  let q = c
    .from("lookup_options")
    .select(
      "id, category, value, sort_order, external_ref, is_system, app_visible, deactivated, option_category"
    )
    .eq("category", category);
  if (!opts.includeInactive) q = q.eq("deactivated", false);
  // Default: only Request-platform values. includeHidden reveals app_visible=false
  // rows (e.g. Unleashed-only codes) for the management screen.
  if (!opts.includeHidden) q = q.eq("app_visible", true);
  const { data, error } = await q
    .order("sort_order", { ascending: true })
    .order("value", { ascending: true });
  if (error) {
    logger.warn(`listLookupOptions(${category}) failed:`, error.message);
    return null;
  }
  return (data ?? []) as LookupOption[];
}

// The broad option_category (db-124) for a single value in a list — e.g. the
// customer_type value's category (DFV Refuge / Intensive Support Org. / …).
// null when the value isn't found or has no category set.
export async function getOptionCategory(
  category: string,
  value: string | null | undefined
): Promise<string | null> {
  const c = getTypedClient();
  if (!c || !value) return null;
  const { data, error } = await c
    .from("lookup_options")
    .select("option_category")
    .eq("category", category)
    .eq("value", value)
    .limit(1);
  if (error) {
    logger.warn(`getOptionCategory(${category}) failed:`, error.message);
    return null;
  }
  const rows = data as unknown as Array<{ option_category: string | null }> | null;
  return rows?.[0]?.option_category ?? null;
}

// customer_type value → its broad option_category (db-124), plus the sorted
// distinct category list. Used by the "Customer type" filter on the Audience /
// Feedback / Telemarketing screens, which now filter by CATEGORY (DFV Refuge /
// Intensive Support Org. / First Nation / Other) rather than the raw value. I/O
// here; the pure shelter filter stays pure — the caller enriches each shelter with
// `customer_category` from `byType`. Hidden (app_visible=false) Unleashed-code
// values are included so every stored customer_type maps to its category.
export async function loadCustomerTypeCategories(): Promise<{
  byType: Map<string, string>;
  categories: string[];
}> {
  const opts = (await listLookupOptions("customer_type", { includeHidden: true })) ?? [];
  const byType = new Map<string, string>();
  const cats = new Set<string>();
  for (const o of opts) {
    if (o.value && o.option_category) {
      byType.set(o.value, o.option_category);
      cats.add(o.option_category);
    }
  }
  return { byType, categories: [...cats].sort() };
}

// Just the active value strings for a category (for simple dropdowns).
export async function listLookupValues(category: string): Promise<string[] | null> {
  const opts = await listLookupOptions(category);
  if (!opts) return null;
  return opts.map((o) => o.value).filter((v): v is string => !!v);
}

// Add an option. Returns { error } (mirrors the other repos).
export async function insertLookupOption(
  category: string,
  value: string,
  extra: { externalRef?: string | null; sortOrder?: number; appVisible?: boolean } = {}
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const row: {
    category: string;
    value: string;
    external_ref?: string | null;
    sort_order?: number;
    app_visible?: boolean;
  } = { category, value };
  if (extra.externalRef !== undefined) row.external_ref = extra.externalRef;
  if (extra.sortOrder !== undefined) row.sort_order = extra.sortOrder;
  if (extra.appVisible !== undefined) row.app_visible = extra.appVisible;
  const { error } = await c.from("lookup_options").insert([row]);
  if (error) logger.warn(`insertLookupOption(${category}) failed:`, error.message);
  return { error };
}

// Back-compat convenience for the simple "add a value" flow.
export async function insertLookupValue(
  category: string,
  value: string
): Promise<{ error: { message: string } | null }> {
  return insertLookupOption(category, value);
}

// Update an option by id (rename / re-order / re-ref / deactivate).
export async function updateLookupOption(
  id: string,
  patch: {
    value?: string;
    external_ref?: string | null;
    sort_order?: number;
    app_visible?: boolean;
    deactivated?: boolean;
    option_category?: string | null;
  }
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("lookup_options").update(patch).eq("id", id);
  if (error) logger.warn(`updateLookupOption(${id}) failed:`, error.message);
  return { error };
}

// Soft-delete / restore an option.
export async function deactivateLookupOption(
  id: string,
  deactivated = true
): Promise<{ error: { message: string } | null }> {
  return updateLookupOption(id, { deactivated });
}
